import React, { useState, useEffect } from 'react';
import { styled } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getImageSrc } from '../../utils/getURL';
import { ButtonBm, SelectBm } from '../@material-extend';
import { usePutFarmerBankStatus } from '../api/mutations/hooks';
import { useSnakBarTran } from '../../hook';
import { ImagesBM } from '../imagesBM';
import { useBankTypes } from '../api/queries/hooks';
import { capitalizeFullName } from '../../utils/common';


// // ----------------- Added Helper Function -----------------
// const capitalizeFullName = (name) => {
//   if (!name) return "";
//   return name
//     .trim()
//     .split(" ")
//     .filter(word => word.length > 0)
//     .map(word => word[0].toUpperCase() + word.slice(1).toLowerCase())
//     .join(" ");
// };
// ----------------------------------------------------------

// Styled Components
const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  marginTop: theme.spacing(3),
  boxShadow: theme.shadows[4],
  borderRadius: theme.spacing(1),
  overflow: 'hidden'
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: 'rgba(145, 158, 171, 0.2)',
  '& .MuiTableCell-head': {
    fontWeight: 700,
    fontSize: '0.95rem',
    padding: theme.spacing(2)
  }
}));

const StyledTableRow = styled(TableRow)(({ theme, isLast }) => ({
  width: '100%',
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  '&:hover': {
    backgroundColor: isLast ? '' : theme.palette.action.selected,
    transition: 'background-color 0.2s ease'
  }
}));

const DetailCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  width: '30%',
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`
}));

const ValueCell = styled(TableCell)(({ theme }) => ({
  color: theme.palette.text.secondary,
  padding: theme.spacing(2),
  fontSize: '0.9rem'
}));

const ProofImage = styled('img')(({ theme }) => ({
  width: '100%',
  height: '300px',
  objectFit: "contain",
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  border: `2px solid ${theme.palette.divider}`,
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.spacing(1),
  boxShadow: theme.shadows[2]
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  marginTop: theme.spacing(1),
  fontWeight: 600
}));

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    width: '50%',
    maxHeight: '90vh'
  }
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500]
}));

// StatusBox with color logic - ADDED ON_HOLD COLOR
const StatusBox = styled(Box)(({ theme, status }) => {
  let bgColor = '#999';
  if (status === 'BANK_DOCUMENT_REQUESTED') bgColor = '#FFC107';
  else if (status === 'BANK_DOCUMENT_RECEIVED') bgColor = '#2196F3';
  else if (status === 'BANK_DOCUMENT_VERIFIED') bgColor = '#4CAF50';
  else if (status === 'BANK_DOCUMENT_REJECTED') bgColor = '#F44336';
  else if (status === 'NOT_ELIGIBLE') bgColor = '#9E9E9E';
  else if (status === 'ON_HOLD') bgColor = '#FF9800'; // ADDED THIS LINE

  return {
    backgroundColor: bgColor,
    color: '#fff',
    fontWeight: 600,
    padding: '6px 12px',
    borderRadius: '12px',
    display: 'inline-block',
    marginBottom: '16px'
  };
});

const BANK_PROOF_TYPE = Object.freeze({
  CANCELLED_CHEQUE: 'CANCELLED_CHEQUE',
  BANK_PASSBOOK: 'BANK_PASSBOOK',
  BANK_STATEMENT: 'BANK_STATEMENT'
});

const BANK_PROOF_LABEL = Object.freeze({
  BANK_PASSBOOK: 'Passbook',
  BANK_STATEMENT: 'Bank Statement'
});

// ADDED ON_HOLD to BankDocumentStatus
const BankDocumentStatus = Object.freeze({
  REQUESTED: 'BANK_DOCUMENT_REQUESTED',
  RECEIVED: 'BANK_DOCUMENT_RECEIVED',
  REJECTED: 'BANK_DOCUMENT_REJECTED',
  VERIFIED: 'BANK_DOCUMENT_VERIFIED',
  INELIGIBLE: 'NOT_ELIGIBLE',
  ON_HOLD: 'ON_HOLD'
});

// Friendly labels for displaying
const BankDocumentStatusLabel = Object.freeze({
  BANK_DOCUMENT_REQUESTED: 'Farmer yet to upload bank details',
  BANK_DOCUMENT_RECEIVED: 'Bank details received',
  BANK_DOCUMENT_REJECTED: 'Bank details rework requested',
  BANK_DOCUMENT_VERIFIED: 'Bank details QA approved',
  NOT_ELIGIBLE: 'Bank details not requested',
  ON_HOLD: 'Bank details on hold for re-verification'
});

export default function BankDetailsComparison({ bankData, data, fetchData }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [newData, oldData] = [bankData?.new, bankData?.old];
  const [bankDocumentType, setBankDocumentType] = useState(bankData?.bankDocumentType || '');
  const [bankImageUrl, setBankImageUrl] = useState({});
  const { mutate, isPending } = usePutFarmerBankStatus();
  const { showSuccessSnackbar, showErrorSnackbar } = useSnakBarTran();
  const { data: bankDoc } = useBankTypes();

  const details = [
    { label: 'Beneficiary First Name', key: 'bankAccountHolderName' },
    { label: 'Bank Account Number', key: 'bankAccount' },
    { label: 'Bank IFSC Code', key: 'bankCode' },
    { label: 'Bank Name', key: 'bankName' },
    { label: 'Bank Detail Proof Image', key: 'bankFileId' }
  ];

  // Check if current status is ON_HOLD - Disable only the "On Hold" button
  const isOnHoldStatus = data?.bankStatus === BankDocumentStatus.ON_HOLD;

  // Disable actions when status is neither RECEIVED nor ON_HOLD
  const disableActions =
    data?.bankStatus !== BankDocumentStatus.RECEIVED &&
    data?.bankStatus !== BankDocumentStatus.ON_HOLD;

  const renderValue = (value, key) => {
    if (key === 'bankAccountHolderName') {
      return <Typography variant="body2">{capitalizeFullName(value)}</Typography>;
    }

    if (key === 'bankFileId') {
      return value ? <ImagesBM url={value} /> : null;
    }

    return <Typography variant="body2">{value}</Typography>;
  };
  // ------------------------------------------------------

  const handleImageClick = (imageUrl) => setSelectedImage(imageUrl);
  const handleCloseDialog = () => setSelectedImage(null);

  const handleBankDocApprove = () => {
    if (!bankDocumentType) {
      showErrorSnackbar({ text: 'Please select bank document type' });
      return;
    }
    mutate(
      {
        farmerId: data?.farmerId,
        status: BankDocumentStatus.VERIFIED,
        documentType: bankDocumentType
      },
      {
        onSuccess: () => {
          showSuccessSnackbar({ text: 'Bank details approved successfully' });
          fetchData();
        }
      }
    );
  };

  const handleBankDocReject = () => {
    if (!bankDocumentType) {
      showErrorSnackbar({ text: 'Please select bank document type' });
      return;
    }
    mutate(
      {
        farmerId: data.farmerId,
        status: BankDocumentStatus.REJECTED,
        documentType: bankDocumentType
      },
      {
        onSuccess: () => {
          showSuccessSnackbar({ text: 'Bank details rejected successfully' });
          fetchData();
        }
      }
    );
  };

  // ADDED THIS FUNCTION - Handle Mark On Hold
  const handleMarkOnHold = () => {
    mutate(
      {
        farmerId: data?.farmerId,
        status: BankDocumentStatus.ON_HOLD,
        documentType: bankDocumentType || bankData?.bankDocumentType
      },
      {
        onSuccess: () => {
          showSuccessSnackbar({ text: 'Bank details marked as on hold' });
          fetchData();
        }
      }
    );
  };

  const getUrl = async (path) => {
    if (!path) return;
    const imgPath = await getImageSrc([path]);
    return imgPath;
  };

  const fetchImages = async () => {
    const newImage = await getUrl(bankData?.new?.bankFileId);
    const oldImage = await getUrl(bankData?.old?.bankFileId);
    setBankImageUrl({ new: newImage, old: oldImage });
  };

  useEffect(() => {
    if (!bankData) return;
    fetchImages();
  }, [bankData]);

  return (
    <Paper elevation={10} sx={{ padding: '1rem', marginTop: '0.2rem', width: '100%' }}>
      <Typography
        variant="h6"
        sx={{ fontSize: '18px', color: '#212B36' }}
        gutterBottom
        color="primary"
        fontWeight={700}
      >
        Bank Details
      </Typography>

      {/* Status Box with friendly label */}
      <StatusBox status={data?.bankStatus}>{BankDocumentStatusLabel[data?.bankStatus]}</StatusBox>

      <StyledTableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{ borderRight: `1px solid rgba(145, 158, 171, 0.24)` }}>
                Details
              </TableCell>
              <TableCell>Bank Details Given During Onboarding</TableCell>
              <TableCell>Latest Bank Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {details.map((detail, idx) => (
              <StyledTableRow isLast={idx === details.length - 1} key={detail?.key}>
                {detail.key !== 'bankFileId' ? (
                  <>
                    <DetailCell>{detail.label}</DetailCell>
                    <ValueCell>{renderValue(oldData?.[detail?.key], detail?.key)}</ValueCell>
                    <ValueCell>{renderValue(newData?.[detail?.key], detail?.key)}</ValueCell>
                  </>
                ) : (
                  <ValueCell colSpan={3}>
                    <Box display="flex" flexDirection="column" gap="1rem">
                      {oldData?.[detail?.key] && (
                        <Box sx={{ flex: 1 }}>
                          {renderValue(bankImageUrl.old, detail?.key)}
                          <Typography>Previous</Typography>
                        </Box>
                      )}
                      {newData?.[detail?.key] && (
                        <Box sx={{ flex: 1 }}>
                          {renderValue(bankImageUrl.new, detail?.key)}
                          <Typography>Latest</Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <SelectBm
                          disabled={disableActions}
                          containerSx={{ width: '250px !important', padding: '0 !important' }}
                          value={bankDocumentType || ''}
                          onChange={({ target }) => setBankDocumentType(target.value)}
                          label="Select document type"
                          list={
                            bankDoc?.map((doc) => ({ label: doc.label, value: doc.value })) || []
                          }
                        />

                        <ButtonBm
                          disabled={disableActions}
                          elevation={0}
                          sx={{
                            boxShadow: 'none',
                            width: '100px',
                            borderRadius: '20px',
                            backgroundColor: '#004752'
                          }}
                          onClick={handleBankDocApprove}
                        >
                          Approve
                        </ButtonBm>
                        <ButtonBm
                          disabled={disableActions}
                          elevation={0}
                          sx={{
                            boxShadow: 'none',
                            width: '100px',
                            borderRadius: '20px',
                            backgroundColor: '#e30000',
                            '&:hover': { backgroundColor: '#e30000' }
                          }}
                          onClick={handleBankDocReject}
                        >
                          Reject
                        </ButtonBm>
                        {/* ADDED THIS BUTTON - Mark On Hold */}
                        {/* Disabled when status is already ON_HOLD */}
                        <ButtonBm
                          disabled={disableActions || isOnHoldStatus} // Disable if already ON_HOLD
                          elevation={0}
                          sx={{
                            boxShadow: 'none',
                            width: '120px',
                            borderRadius: '20px',
                            backgroundColor: isOnHoldStatus ? '#CCCCCC' : '#FF9800', // Grey when disabled
                            color: '#fff',
                            '&:hover': isOnHoldStatus ? {} : { backgroundColor: '#F57C00' }
                          }}
                          onClick={handleMarkOnHold}
                        >
                          On Hold
                        </ButtonBm>
                        {isPending && <CircularProgress size={30} />}
                      </Box>
                    </Box>
                  </ValueCell>
                )}
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>

      <StyledDialog open={Boolean(selectedImage)} onClose={handleCloseDialog} maxWidth="lg">
        <CloseButton onClick={handleCloseDialog}>
          <CloseIcon />
        </CloseButton>
        <DialogContent sx={{ p: 2 }}>
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Enlarged proof"
              style={{
                width: '100%',
                maxHeight: '100vh',
                borderRadius: '8px'
              }}
            />
          )}
        </DialogContent>
      </StyledDialog>
    </Paper>
  );
}
